import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import {
  Button,
  Description,
  FieldError,
  Form,
  Heading,
  Input,
  Label,
  Separator,
  Spinner,
  TextField,
  useOverlayState,
} from '@heroui/react';
import { updateMe } from '../../../api/user';
import { AsyncButton } from '../../../components/ui/async-button';
import { PhoneVerificationModal } from '../../profile/components/phone-verification-modal';
import {
  emptyProfileForm,
  validateProfileForm,
  type ProfileFormFields,
} from '../../profile/utils/profile-form';
import { refreshUserDoc, useAuth } from '../../../hooks/useAuth';
import { doSignOut, refreshUser } from '../../../lib/firebase';
import { isPhoneVerifiedOnFirebase } from '../../../lib/firebase-phone';
import { showErrorFromUnknown, showSuccess } from '../../../lib/toast';
import type { Customer } from '../../../types/domain';
import { isDocumentRequired } from '../../../utils/profileValidation';
import { formatDocument, formatPhone } from '../../../utils/validators';
import { AuthSplitLayout } from '../components/auth-split-layout';
import { getPostAuthPath } from '../utils/post-auth-redirect';

type FormFields = Customer;

function getGooglePrefill(
  authUser: NonNullable<ReturnType<typeof useAuth>['authUser']>,
  userDoc: NonNullable<ReturnType<typeof useAuth>['userDoc']>,
): Partial<FormFields> {
  const isGoogle = authUser.providerData.some((p) => p.providerId === 'google.com');
  if (!isGoogle) return {};

  const firstName = userDoc.firstName || userDoc.customer?.firstName;
  const lastName = userDoc.lastName || userDoc.customer?.lastName;

  if (firstName || lastName) {
    return { firstName: firstName ?? '', lastName: lastName ?? '', documentNumber: '', phone: '' };
  }

  if (userDoc.displayName) {
    const parts = userDoc.displayName.trim().split(/\s+/);
    return {
      firstName: parts[0] ?? '',
      lastName: parts.slice(1).join(' '),
      documentNumber: '',
      phone: '',
    };
  }

  return {};
}

export default function CompleteUserPage() {
  const { authUser, userDoc, loading: authLoading, setAuthUser } = useAuth();
  const navigate = useNavigate();
  const verificationModal = useOverlayState();
  const requireDocument = isDocumentRequired();

  const [form, setForm] = useState<FormFields>(emptyProfileForm());
  const [submitting, setSubmitting] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof FormFields, string>>>({});
  const [prefilled, setPrefilled] = useState(false);

  useEffect(() => {
    if (!userDoc || !authUser || prefilled) return;
    const prefill = getGooglePrefill(authUser, userDoc);
    if (Object.keys(prefill).length > 0) {
      setForm((prev) => ({ ...prev, ...prefill }));
    }
    setPrefilled(true);
  }, [authUser, userDoc, prefilled]);

  useEffect(() => {
    if (!attemptedSubmit) return;
    setFieldErrors(validateProfileForm(form));
  }, [form, attemptedSubmit]);

  const saveProfile = async () => {
    await updateMe({
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      phone: form.phone,
      documentNumber: form.documentNumber.trim() || undefined,
    });

    const updated = await refreshUserDoc();
    showSuccess('Perfil completado com sucesso!');
    navigate(getPostAuthPath(updated), { replace: true });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!authUser || submitting) return;

    setAttemptedSubmit(true);
    const errors = validateProfileForm(form);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const verified = isPhoneVerifiedOnFirebase(authUser, form.phone ?? '');
    if (!verified) {
      verificationModal.open();
      return;
    }

    setSubmitting(true);
    try {
      await saveProfile();
    } catch (err) {
      showErrorFromUnknown(err, 'Erro ao salvar dados');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerified = async () => {
    if (authUser) {
      const refreshed = await refreshUser(authUser);
      setAuthUser(refreshed);
    }

    setSubmitting(true);
    try {
      await saveProfile();
    } catch (err) {
      showErrorFromUnknown(err, 'Erro ao salvar dados');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await doSignOut();
      navigate('/login', { replace: true });
    } catch (err) {
      showErrorFromUnknown(err, 'Erro ao sair');
    }
  };

  if (authLoading) {
    return (
      <AuthSplitLayout>
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      </AuthSplitLayout>
    );
  }

  if (!authUser) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AuthSplitLayout>
      <div className="w-full max-w-sm space-y-6">
        <div className="flex w-full flex-col items-center space-y-1 text-center lg:items-start lg:text-left">
          <Heading level={2} className="w-full text-center text-2xl font-semibold lg:text-left">
            Complete seu perfil
          </Heading>
          <Description className="text-center lg:text-left">
            Preencha seus dados e confirme seu telefone por SMS para continuar.
          </Description>
        </div>

        <Form className="flex flex-col gap-5" onSubmit={handleSubmit}>
          <TextField
            name="firstName"
            fullWidth
            isInvalid={attemptedSubmit && !!fieldErrors.firstName}
          >
            <Label>Nome</Label>
            <Input
              fullWidth
              value={form.firstName}
              onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
              autoComplete="given-name"
              className="h-11 text-base"
            />
            {attemptedSubmit && fieldErrors.firstName ? (
              <FieldError>{fieldErrors.firstName}</FieldError>
            ) : null}
          </TextField>

          <TextField
            name="lastName"
            fullWidth
            isInvalid={attemptedSubmit && !!fieldErrors.lastName}
          >
            <Label>Sobrenome</Label>
            <Input
              fullWidth
              value={form.lastName}
              onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
              autoComplete="family-name"
              className="h-11 text-base"
            />
            {attemptedSubmit && fieldErrors.lastName ? (
              <FieldError>{fieldErrors.lastName}</FieldError>
            ) : null}
          </TextField>

          <TextField
            name="documentNumber"
            fullWidth
            isInvalid={attemptedSubmit && !!fieldErrors.documentNumber}
          >
            <Label>CPF{requireDocument ? '' : ' (opcional)'}</Label>
            <Input
              fullWidth
              value={form.documentNumber}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  documentNumber: formatDocument(e.target.value, 'CPF'),
                }))
              }
              placeholder="000.000.000-00"
              inputMode="numeric"
              className="h-11 text-base"
              data-clarity-mask="true"
            />
            {!requireDocument ? (
              <Description className="text-xs">Opcional em staging/local</Description>
            ) : null}
            {attemptedSubmit && fieldErrors.documentNumber ? (
              <FieldError>{fieldErrors.documentNumber}</FieldError>
            ) : null}
          </TextField>

          <TextField name="phone" fullWidth isInvalid={attemptedSubmit && !!fieldErrors.phone}>
            <Label>Telefone</Label>
            <Input
              fullWidth
              value={form.phone ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, phone: formatPhone(e.target.value) }))}
              placeholder="(11) 99999-9999"
              inputMode="tel"
              autoComplete="tel"
              className="h-11 text-base"
              data-clarity-mask="true"
            />
            {attemptedSubmit && fieldErrors.phone ? (
              <FieldError>{fieldErrors.phone}</FieldError>
            ) : (
              <Description className="text-xs">
                Enviaremos um código SMS para confirmar este número.
              </Description>
            )}
          </TextField>

          <AsyncButton type="submit" variant="primary" size="lg" fullWidth isPending={submitting}>
            Continuar
          </AsyncButton>
        </Form>

        <div className="flex items-center gap-3">
          <Separator className="flex-1" />
          <span className="text-muted text-xs uppercase tracking-wide">ou</span>
          <Separator className="flex-1" />
        </div>

        <Button variant="ghost" size="lg" fullWidth onPress={handleLogout}>
          <LogOut size={18} />
          Usar outra conta
        </Button>
      </div>

      <PhoneVerificationModal
        state={verificationModal}
        phone={form.phone ?? ''}
        onVerified={handleVerified}
      />
    </AuthSplitLayout>
  );
}
